import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { openAiAssistant } from '../utils/aiAssistantEvents'
import {
  isPromoBannerEnabled,
  PROMO_BANNER_PREF_EVENT,
} from '../utils/promoBannerPrefs'

const ROTATE_MS = 5500

type PromoAction = 'sell' | 'ai' | 'browse'

type PromoSlide = {
  messageKey: string
  ctaKey: string
  action: PromoAction
  /** Helpo24 navy / blue / teal variants only */
  theme: 'navy' | 'blue' | 'teal' | 'deep'
}

const PROMO_SLIDES: PromoSlide[] = [
  {
    messageKey: 'promo.slide.sell.message',
    ctaKey: 'promo.slide.sell.cta',
    action: 'sell',
    theme: 'navy',
  },
  {
    messageKey: 'promo.slide.ai.message',
    ctaKey: 'promo.slide.ai.cta',
    action: 'ai',
    theme: 'blue',
  },
  {
    messageKey: 'promo.slide.browse.message',
    ctaKey: 'promo.slide.browse.cta',
    action: 'browse',
    theme: 'teal',
  },
  {
    messageKey: 'promo.slide.trust.message',
    ctaKey: 'promo.slide.trust.cta',
    action: 'browse',
    theme: 'deep',
  },
]

interface PromoBannerProps {
  onAction: () => void
  onOpenCatalog?: () => void
}

export default function PromoBanner({ onAction, onOpenCatalog }: PromoBannerProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(isPromoBannerEnabled)
  const [active, setActive] = useState(0)

  useEffect(() => {
    function onPrefChange() {
      setVisible(isPromoBannerEnabled())
    }
    window.addEventListener(PROMO_BANNER_PREF_EVENT, onPrefChange)
    window.addEventListener('storage', onPrefChange)
    return () => {
      window.removeEventListener(PROMO_BANNER_PREF_EVENT, onPrefChange)
      window.removeEventListener('storage', onPrefChange)
    }
  }, [])

  useEffect(() => {
    if (!visible) return
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % PROMO_SLIDES.length)
    }, ROTATE_MS)
    return () => window.clearInterval(id)
  }, [visible])

  const runSlideAction = useCallback(
    (action: PromoAction) => {
      if (action === 'ai') {
        openAiAssistant()
        return
      }
      if (action === 'browse') {
        onOpenCatalog?.()
        return
      }
      onAction()
    },
    [onAction, onOpenCatalog],
  )

  if (!visible) return null

  const slide = PROMO_SLIDES[active]

  return (
    <aside
      className={`promo-banner promo-banner--${slide.theme}`}
      role="region"
      aria-label={t('promo.ariaLabel')}
      aria-live="polite"
    >
      <button
        type="button"
        className="promo-banner__main"
        onClick={() => runSlideAction(slide.action)}
        aria-label={t(slide.messageKey)}
      >
        <span className="promo-banner__text">{t(slide.messageKey)}</span>
      </button>
      <div className="promo-banner__dots" role="tablist" aria-label={t('promo.ariaLabel')}>
        {PROMO_SLIDES.map((s, i) => (
          <button
            key={s.messageKey}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={t(s.messageKey)}
            className={`promo-banner__pager${i === active ? ' promo-banner__pager--on' : ''}`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>
    </aside>
  )
}
